import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';

import PageLayout from '../components/PageLayout';
import IconButton from '../components/ui/IconButton';
import useAuth from '../auth/useAuth';
import { getAboutPage, updateAboutPage } from '../api';
import { markdownUrlTransform } from '../utils/markdownUrl';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';

const FEATURE_ICONS = [
  'fa-solid fa-book',
  'fa-solid fa-user-shield',
  'fa-solid fa-magnifying-glass',
  'fa-solid fa-bullhorn',
  'fa-solid fa-pen-ruler',
  'fa-solid fa-file-import',
  'fa-solid fa-clock-rotate-left',
  'fa-solid fa-universal-access',
];

const STEP_ICONS = [
  'fa-solid fa-magnifying-glass',
  'fa-solid fa-book-open',
  'fa-solid fa-star',
  'fa-solid fa-pen-to-square',
];

const asList = (value) => (Array.isArray(value) ? value : []);

function DefaultAboutContent({ t }) {
  const features = t('about.features', { returnObjects: true });
  const steps = asList(t('about.steps', { returnObjects: true }));
  const faq = asList(t('about.faq', { returnObjects: true }));

  return (
    <div className="about-default">
      <div className="about-features">
        {Array.isArray(features) &&
          features.map((feature, index) => (
            <section
              key={feature.title}
              className="about-feature"
              aria-labelledby={`about-feature-${index}-title`}
            >
              <span className="about-feature__icon" aria-hidden="true">
                <i className={FEATURE_ICONS[index % FEATURE_ICONS.length]} />
              </span>

              <h2 id={`about-feature-${index}-title`} className="about-feature__title">
                {feature.title}
              </h2>

              <p className="about-feature__text">{feature.text}</p>
            </section>
          ))}
      </div>

      {steps.length > 0 && (
        <section className="about-section" aria-labelledby="about-steps-title">
          <h2 id="about-steps-title" className="about-section__title">{t('about.stepsTitle')}</h2>

          <ol className="about-steps">
            {steps.map((step, index) => (
              <li key={step.title} className="about-step">
                <span className="about-step__number" aria-hidden="true">{index + 1}</span>
                <i className={`${STEP_ICONS[index % STEP_ICONS.length]} about-step__icon`} aria-hidden="true" />
                <h3 className="about-step__title">{step.title}</h3>
                <p className="about-step__text">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {faq.length > 0 && (
        <section className="about-section" aria-labelledby="about-faq-title">
          <h2 id="about-faq-title" className="about-section__title">{t('about.faqTitle')}</h2>

          <div className="about-faq">
            {faq.map((item) => (
              <details key={item.question} className="about-faq__item">
                <summary className="about-faq__question">
                  {item.question}
                  <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                </summary>
                <p className="about-faq__answer">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="about-contact" aria-labelledby="about-contact-title">
        <h2 id="about-contact-title" className="about-contact__title">
          <i className="fa-solid fa-envelope" aria-hidden="true" />
          {t('about.contactTitle')}
        </h2>

        <p className="about-contact__text">{t('about.contactText')}</p>
      </section>
    </div>
  );
}

function About() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.rights?.includes('admin.access') ?? false;

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await getAboutPage();

        if (!cancelled) {
          setContent(data?.content ?? null);
        }
      } catch (err) {
        console.error('Failed to load about page:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  function startEditing() {
    setEditText(content ?? '');
    setSaveError('');
    setIsEditing(true);
  }

  function cancelEditing() {
    setIsEditing(false);
    setSaveError('');
  }

  async function handleSave() {
    try {
      setIsSaving(true);
      setSaveError('');

      const updated = await updateAboutPage(editText);

      setContent(updated?.content ?? null);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save about page:', err);

      setSaveError(
        err?.message || t('about.saveErrorFallback'),
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <PageLayout
      extraSidebarItems={[
        {
          to: '/about',
          label: t('common.about'),
          icon: 'fa-solid fa-circle-info',
        },
      ]}
    >
      <PageMain>
        <PageHeader
          eyebrow={t('about.eyebrow')}
          title={t('about.title')}
          titleId="about-page-title"
          description={!isEditing && !content && t('about.intro')}
          actions={isAdmin && !isEditing && (
            <IconButton
              className="article-edit-button"
              icon="fa-solid fa-pen-to-square"
              label={t('about.editButtonLabel')}
              onClick={startEditing}
            />
          )}
        />

        <PageBody
          className="account-page"
          aria-labelledby="about-page-title"
        >

          {isEditing ? (
            <div className="maincol markdown-content article-editor">
              <p className="about-edit-hint">
                {t('about.editHint')}
              </p>

              <div
                data-color-mode="dark"
                className="article-editor-md"
              >
                <MDEditor
                  value={editText}
                  onChange={(value) => {
                    setEditText(value ?? '');
                    setSaveError('');
                  }}
                  height={520}
                  minHeight={300}
                  visibleDragbar
                  preview="edit"
                  highlightEnable={false}
                />
              </div>

              {saveError && (
                <div
                  className="login-message login-message-error"
                  role="alert"
                >
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{saveError}</span>
                </div>
              )}

              <div className="article-editor-actions">
                <Button
                  variant="secondary"
                  onClick={cancelEditing}
                  disabled={isSaving}
                >
                  {t('about.cancel')}
                </Button>

                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving
                    ? t('about.saving')
                    : t('about.save')}
                </Button>
              </div>
            </div>
          ) : loading ? null : content ? (
            <div className="maincol markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={markdownUrlTransform}>
                {content}
              </ReactMarkdown>
            </div>
          ) : (
            <DefaultAboutContent t={t} />
          )}
        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default About;
